// 261010 UI-assettabs — tab Bối cảnh của bước Tài sản: MỘT panel đầy bề rộng cho MỖI bối cảnh.
//   Đầu: tên + chip trạng thái | «Gom hướng» · «Tạo hướng còn thiếu» · (dừng hàng chờ khi đang chạy) · ⋯
//   Thân hai cột: ảnh chuẩn lớn (tự duyệt khi lưu) | «Ảnh theo hướng» (lưới ô hướng). Chân: mô tả · bố cục · khu vực · đề xuất gom hướng · shot đang cần.
// 🔴 Nhóm hướng gắn với bối cảnh bằng locationId: hook `useWorkflowLocationDirections` và `LocationAssetCard` đều nhận ĐÚNG `row.locationId` của bối cảnh đang vẽ,
// mỗi bối cảnh một panel (key = location.id) — không có state/nhóm dùng chung giữa các bối cảnh.
import React, { useRef, useState } from 'react';
import { Input, Spin } from 'antd';
import type { FilmLocation, FilmLocationZone, ImageModelInfo } from '../../types/film';
import type { LocationNeedsLocation, LocationView } from '../../types/filmLocationAssets';
import FilmAssetCard, { type AssetField } from './FilmAssetCard';
import LocationAssetCard from './LocationAssetCard';
import { cardViewsProjection, ctxViewsProjection, newViewsRefreshMemo, resolveViewsRefreshKey } from './locationViewsRefreshKey';
import { LocationDirectionsActions, LocationDirectionsProposal, useWorkflowLocationDirections } from './WorkflowLocationDirections';

export interface LocationPanelProps {
  location: FilmLocation;
  /** Dòng `location-needs` của CHÍNH bối cảnh này (`needs.locations.find(x => x.locationId === location.id)`); null = chưa có / không có shot trong plan. */
  row: LocationNeedsLocation | null;
  needsLoaded: boolean;
  planId: string;
  models: ImageModelInfo[];
  busy: 'gen' | 'geo' | 'upload' | 'delete' | 'visual' | null;
  visualWarnings?: string[];
  onSaveField: (field: AssetField, value: string) => Promise<void>;
  onSaveZones: (zones: FilmLocationZone[]) => Promise<void>;
  onGen: () => Promise<void>;
  onGenVisual: () => Promise<void>;
  onGenGeometry: () => Promise<void>;
  onGenWithModel: (modelId?: string) => Promise<void>;
  onUpload: (file: File) => Promise<void>;
  onOpenStudio: () => void;
  onDelete: () => void;
  /** Sau mọi thao tác ghi: tải lại workflow-status / chuông / dải chuẩn bị. */
  onActionDone: () => void;
}

/** Tên bối cảnh trên đầu panel — bấm để sửa tại chỗ (cùng đường `onSaveField('name')` như thẻ cũ). */
const PanelTitle: React.FC<{ name: string; saveName: (v: string) => Promise<void> }> = ({ name, saveName }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const [saving, setSaving] = useState(false);
  const commit = async () => {
    const v = draft.trim();
    setEditing(false);
    if (!v || v === name) return;
    setSaving(true);
    try { await saveName(v); } finally { setSaving(false); }
  };
  return editing
    ? <Input autoFocus size="small" style={{ maxWidth: 320 }} value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit} onPressEnter={commit} onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }} />
    : <h3 title={name} onClick={() => { setDraft(name); setEditing(true); }}>{saving ? <Spin size="small" /> : name}</h3>;
};

const WithGroup: React.FC<LocationPanelProps & { row: LocationNeedsLocation }> = (p) => {
  // Hook gọi với locationId của dòng needs = CHÍNH bối cảnh `p.location` (cha đã find theo location.id).
  const ctx = useWorkflowLocationDirections(p.planId, p.row.locationId, p.onActionDone);
  // 261029 P1 B2d: khoá nạp lại views của thẻ không đổi chỉ vì ctx hydrate lần đầu (xem locationViewsRefreshKey).
  const refreshMemo = useRef(newViewsRefreshMemo());
  // Bản views mà THẺ đã nạp thật (báo qua onViewsLoaded) — so với bản ctx trước khi bỏ lượt nạp lại (xem locationViewsRefreshKey).
  const [cardViews, setCardViews] = useState<LocationView[] | null>(null);
  const part = (which: 'actions' | 'menu' | 'info') => (
    <FilmAssetCard board part={which} kind="location" item={p.location} models={p.models} busy={p.busy}
      onSaveField={p.onSaveField} onSaveZones={p.onSaveZones} onGen={p.onGen} onGenVisual={p.onGenVisual} visualWarnings={p.visualWarnings}
      onGenWithModel={p.onGenWithModel} onUpload={p.onUpload} onOpenStudio={p.onOpenStudio} onDelete={p.onDelete} onGenGeometry={p.onGenGeometry} />
  );
  return (
    <LocationAssetCard key={`${p.row.locationId}:${p.row.canonical.url ?? ''}:${p.row.canonical.state}`} location={p.row} onActionDone={p.onActionDone} viewsOnly planId={p.planId} onViewsLoaded={setCardViews} refreshKey={resolveViewsRefreshKey(refreshMemo.current,{loading:ctx.loading,raw:JSON.stringify([ctx.phase,ctx.views.map(v=>[v.id,v.status,v.imageUrl])]),ctxProjection:ctxViewsProjection(ctx.views),cardProjection:cardViewsProjection(cardViews,ctx.views)})}
      panel={{
        autoRunning: ctx.phase==='RUNNING'||ctx.phase==='WAITING_BANK',
        title: <PanelTitle name={p.location.name} saveName={(v) => p.onSaveField('name', v)} />,
        actions: <><LocationDirectionsActions ctx={ctx} />{part('menu')}</>,
        canonTools: part('actions'),
        footer: <>{part('info')}<LocationDirectionsProposal ctx={ctx} /></>,
      }} />
  );
};

const WorkflowLocationPanel: React.FC<LocationPanelProps> = (p) => {
  if (p.row) return <WithGroup {...p} row={p.row} />;
  // Chưa có dòng needs của bối cảnh này (đang tải, hoặc bối cảnh không có shot trong plan): chỉ thẻ + ghi chú — KHÔNG có nhóm hướng.
  const part = (which: 'actions' | 'menu' | 'info') => (
    <FilmAssetCard board part={which} kind="location" item={p.location} models={p.models} busy={p.busy}
      onSaveField={p.onSaveField} onSaveZones={p.onSaveZones} onGen={p.onGen} onGenVisual={p.onGenVisual} visualWarnings={p.visualWarnings}
      onGenWithModel={p.onGenWithModel} onUpload={p.onUpload} onOpenStudio={p.onOpenStudio} onDelete={p.onDelete} onGenGeometry={p.onGenGeometry} />
  );
  return (
    <section className="ac-lp" aria-label={`Bối cảnh ${p.location.name}`} data-testid="ac-loc-panel" data-location-id={p.location.id}>
      <header className="ac-lp-h">
        <PanelTitle name={p.location.name} saveName={(v) => p.onSaveField('name', v)} />
        <span className="sp" />
        {part('menu')}
      </header>
      <div className="ac-lp-body">
        <div className="ac-lp-canon">
          <div className="ac-lp-img">
            {p.location.canonicalRefUrl ? <img src={p.location.canonicalRefUrl} alt={`Ảnh chuẩn ${p.location.name}`} style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <div className="empty">Chưa có ảnh chuẩn</div>}
            <div className="ac-lp-ov">{part('actions')}</div>
          </div>
        </div>
        <div className="ac-lp-views">
          <div className="ac-lp-vh"><b>Ảnh theo hướng</b></div>
          <div className="muted" style={{ margin: 0 }}>
            {p.needsLoaded ? 'Máy sẽ tự gom và tạo ảnh hướng' : <><Spin size="small" /> Đang tải bối cảnh của plan…</>}
          </div>
        </div>
      </div>
      <div className="ac-lp-foot">{part('info')}</div>
    </section>
  );
};

export default WorkflowLocationPanel;
